import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { FileText, Search, ChevronLeft, ChevronRight, Edit, Trash2, Check, X, Download } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminReferrals = () => {
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, page_size: 20, total_count: 0, total_pages: 0 });
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedReferrals, setSelectedReferrals] = useState([]);
  const [editDialog, setEditDialog] = useState({ open: false, referral: null });
  const [editForm, setEditForm] = useState({});

  useEffect(() => {
    fetchReferrals();
  }, [pagination.page, statusFilter]);

  const fetchReferrals = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page,
        page_size: pagination.page_size,
        ...(statusFilter && { status: statusFilter })
      });
      
      const res = await axios.get(`${API}/admin/referrals?${params}`, { withCredentials: true });
      setReferrals(res.data.referrals || []);
      setPagination(prev => ({ ...prev, ...res.data.pagination }));
    } catch (err) {
      toast.error("Failed to load referrals");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (referral) => {
    setEditForm({
      plan: referral.plan,
      plan_price: referral.plan_price,
      status: referral.status,
      monthly_commission: referral.monthly_commission
    });
    setEditDialog({ open: true, referral });
  };

  const handleSaveEdit = async () => {
    try {
      await axios.put(`${API}/admin/referrals/${editDialog.referral.referral_id}`, editForm, { withCredentials: true });
      toast.success("Referral updated");
      setEditDialog({ open: false, referral: null });
      fetchReferrals();
    } catch (err) {
      toast.error("Failed to update referral");
    }
  };

  const handleDelete = async (referralId) => {
    if (!window.confirm("Are you sure you want to delete this referral?")) return;
    
    try {
      await axios.delete(`${API}/admin/referrals/${referralId}`, { withCredentials: true });
      toast.success("Referral deleted");
      fetchReferrals();
    } catch (err) {
      toast.error("Failed to delete referral");
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedReferrals.length === 0) {
      toast.error("No referrals selected");
      return;
    }
    
    const actionText = action === "approve" ? "approve" : action === "reject" ? "reject" : "delete";
    if (!window.confirm(`Are you sure you want to ${actionText} ${selectedReferrals.length} referrals?`)) return;
    
    try {
      const res = await axios.post(`${API}/admin/referrals/bulk-action`, {
        referral_ids: selectedReferrals,
        action
      }, { withCredentials: true });
      
      toast.success(`${res.data.results.success} referrals ${actionText}ed`);
      setSelectedReferrals([]);
      fetchReferrals();
    } catch (err) {
      toast.error("Bulk action failed");
    }
  };

  const handleExport = async () => {
    try {
      const res = await axios.get(`${API}/admin/referrals/export/csv`, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'referrals_export.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Export downloaded");
    } catch (err) {
      toast.error("Failed to export");
    }
  };

  const toggleSelect = (referralId) => {
    setSelectedReferrals(prev => 
      prev.includes(referralId) 
        ? prev.filter(id => id !== referralId)
        : [...prev, referralId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedReferrals.length === referrals.length) {
      setSelectedReferrals([]);
    } else {
      setSelectedReferrals(referrals.map(r => r.referral_id));
    }
  };

  const statusColors = {
    active: "text-green-400 bg-green-500/10",
    pending: "text-yellow-400 bg-yellow-500/10",
    churned: "text-red-400 bg-red-500/10"
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <FileText className="h-7 w-7 text-emerald-500" />
            Manage Referrals
          </h1>
          <p className="text-slate-400 mt-1">View and manage all referrals across partners</p>
        </div>
        <Button onClick={handleExport} variant="outline" className="border-slate-600 text-slate-300">
          <Download className="h-4 w-4 mr-2" />
          Export CSV
        </Button>
      </div>

      {/* Filters & Bulk Actions */}
      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="p-4">
          <div className="flex items-center gap-4 flex-wrap">
            <Select 
              value={statusFilter || "all"} 
              onValueChange={(v) => { setStatusFilter(v === "all" ? "" : v); setPagination(prev => ({ ...prev, page: 1 })); }}
            >
              <SelectTrigger className="w-40 bg-slate-700 border-slate-600 text-slate-300">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent className="bg-slate-700 border-slate-600">
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="churned">Churned</SelectItem>
              </SelectContent>
            </Select>

            {selectedReferrals.length > 0 && (
              <div className="flex items-center gap-2 ml-auto">
                <span className="text-sm text-slate-400">{selectedReferrals.length} selected</span>
                <Button 
                  size="sm" 
                  onClick={() => handleBulkAction("approve")}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <Check className="h-4 w-4 mr-1" /> Approve
                </Button>
                <Button 
                  size="sm" 
                  onClick={() => handleBulkAction("reject")}
                  className="bg-yellow-600 hover:bg-yellow-700"
                >
                  <X className="h-4 w-4 mr-1" /> Reject
                </Button>
                <Button 
                  size="sm" 
                  variant="destructive"
                  onClick={() => handleBulkAction("delete")}
                >
                  <Trash2 className="h-4 w-4 mr-1" /> Delete
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Referrals Table */}
      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
            </div>
          ) : referrals.length === 0 ? (
            <p className="text-slate-400 text-center py-16">No referrals found</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-700 bg-slate-900/50">
                    <th className="p-4">
                      <Checkbox
                        checked={selectedReferrals.length === referrals.length && referrals.length > 0}
                        onCheckedChange={toggleSelectAll}
                      />
                    </th>
                    <th className="text-left p-4 text-slate-400 font-medium">Customer</th>
                    <th className="text-left p-4 text-slate-400 font-medium">Partner</th>
                    <th className="text-left p-4 text-slate-400 font-medium">Plan</th>
                    <th className="text-left p-4 text-slate-400 font-medium">Status</th>
                    <th className="text-left p-4 text-slate-400 font-medium">Commission</th>
                    <th className="text-left p-4 text-slate-400 font-medium">Date</th>
                    <th className="text-left p-4 text-slate-400 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {referrals.map((ref) => (
                    <tr key={ref.referral_id} className="border-b border-slate-700 hover:bg-slate-700/30">
                      <td className="p-4">
                        <Checkbox
                          checked={selectedReferrals.includes(ref.referral_id)}
                          onCheckedChange={() => toggleSelect(ref.referral_id)}
                        />
                      </td>
                      <td className="p-4">
                        <div>
                          <p className="text-white font-medium">{ref.referred_name}</p>
                          <p className="text-slate-400 text-sm">{ref.referred_email}</p>
                        </div>
                      </td>
                      <td className="p-4">
                        <div>
                          <p className="text-slate-300 text-sm">{ref.partner_info?.name || "Unknown"}</p>
                          <p className="text-slate-500 text-xs">{ref.source_partner_code}</p>
                        </div>
                      </td>
                      <td className="p-4">
                        <span className="text-white capitalize">{ref.plan}</span>
                        <span className="text-slate-400 text-sm ml-2">${ref.plan_price?.toFixed(2)}</span>
                      </td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded text-xs font-medium capitalize ${statusColors[ref.status]}`}>
                          {ref.status}
                        </span>
                      </td>
                      <td className="p-4">
                        <span className="text-emerald-400 font-medium">${ref.monthly_commission?.toFixed(2)}</span>
                      </td>
                      <td className="p-4">
                        <span className="text-slate-400 text-sm">
                          {new Date(ref.created_at).toLocaleDateString()}
                        </span>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(ref)}
                            className="text-blue-400 hover:text-blue-300"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(ref.referral_id)}
                            className="text-red-400 hover:text-red-300"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {pagination.total_pages > 1 && (
            <div className="flex items-center justify-between p-4 border-t border-slate-700">
              <span className="text-sm text-slate-400">
                Page {pagination.page} of {pagination.total_pages} ({pagination.total_count} total)
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                  disabled={pagination.page <= 1}
                  className="border-slate-600 text-slate-300"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  disabled={pagination.page >= pagination.total_pages}
                  className="border-slate-600 text-slate-300"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={editDialog.open} onOpenChange={(open) => setEditDialog({ open, referral: editDialog.referral })}>
        <DialogContent className="bg-slate-800 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">Edit Referral</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-slate-300">Plan</Label>
              <Select value={editForm.plan} onValueChange={(v) => setEditForm(prev => ({ ...prev, plan: v }))}>
                <SelectTrigger className="mt-1 bg-slate-700 border-slate-600 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-700 border-slate-600">
                  <SelectItem value="free">Free</SelectItem>
                  <SelectItem value="basic">Basic</SelectItem>
                  <SelectItem value="premium">Premium</SelectItem>
                  <SelectItem value="brokerage">Brokerage</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-300">Plan Price ($)</Label>
              <Input
                type="number"
                value={editForm.plan_price}
                onChange={(e) => setEditForm(prev => ({ ...prev, plan_price: parseFloat(e.target.value) || 0 }))}
                className="mt-1 bg-slate-700 border-slate-600 text-white"
              />
            </div>
            <div>
              <Label className="text-slate-300">Status</Label>
              <Select value={editForm.status} onValueChange={(v) => setEditForm(prev => ({ ...prev, status: v }))}>
                <SelectTrigger className="mt-1 bg-slate-700 border-slate-600 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-700 border-slate-600">
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="churned">Churned</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-300">Monthly Commission ($)</Label>
              <Input
                type="number"
                value={editForm.monthly_commission}
                onChange={(e) => setEditForm(prev => ({ ...prev, monthly_commission: parseFloat(e.target.value) || 0 }))}
                className="mt-1 bg-slate-700 border-slate-600 text-white"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog({ open: false, referral: null })} className="border-slate-600 text-slate-300">
              Cancel
            </Button>
            <Button onClick={handleSaveEdit} className="bg-emerald-600 hover:bg-emerald-700">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminReferrals;
